import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FaPlus, FaImage, FaEdit, FaTrash } from "react-icons/fa";
import { toast } from "react-toastify";
import {
  fetchSliders,
  addSlider,
  updateSlider,
  deleteSlider,
  clearState,
} from "../redux/Slices/SliderSlice";
import { fetchCategories } from "../redux/Slices/CategorySlice";
import { useTitle } from "../context/TitleContext";
import PermissionMotionButton from "../components/PermissionMotionButton";
import PermissionButton from "../components/PermissionButton";

const Hero = () => {
  const dispatch = useDispatch();
  const { setTitle } = useTitle();
  const [currentPage, setCurrentPage] = useState(1);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingSlider, setEditingSlider] = useState(null);
  const [deletingSlider, setDeletingSlider] = useState(null);

  const { sliders, meta, loading, error, success } = useSelector(
    (state) => state.sliders
  );
  const { data: categories } = useSelector((state) => state.categories);

  // Form data
  const [formData, setFormData] = useState({
    image: null,
    category_id: "",
    status: 1,
  });

  const [originalData, setOriginalData] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setTitle("السلايدرات الرئيسية");
  }, [setTitle]);

  useEffect(() => {
    dispatch(clearState());
    dispatch(fetchSliders(currentPage));
    dispatch(fetchCategories());
  }, [dispatch, currentPage]);

  // Check for changes
  useEffect(() => {
    if (originalData && showEditForm) {
      const hasFormChanges =
        formData.category_id !== originalData.category_id ||
        formData.status !== originalData.status ||
        formData.image !== null;

      setHasChanges(hasFormChanges);
    } else if (showAddForm) {
      setHasChanges(true);
    }
  }, [formData, originalData, showEditForm, showAddForm]);

  useEffect(() => {
    if (success) {
      setShowAddForm(false);
      setShowEditForm(false);
      setShowDeleteModal(false);
      setEditingSlider(null);
      setDeletingSlider(null);
      setFormData({
        image: null,
        category_id: "",
        status: 1,
      });

      setTimeout(() => {
        toast.success(success, { position: "top-center", zIndex: 9999 });
      }, 100);

      dispatch(fetchSliders(currentPage));
      dispatch(clearState());
    }
    if (error) {
      toast.error(error, { position: "top-center", zIndex: 9999 });
      dispatch(clearState());
    }
  }, [success, error, dispatch, currentPage]);

  const handlePageChange = (page) => {
    if (loading) return;
    if (page === currentPage) return;
    setCurrentPage(page);
  };

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: files ? files[0] : value,
    }));
  };

  const handleAdd = () => {
    setShowAddForm(true);
    setShowEditForm(false);
    setFormData({
      image: null,
      category_id: "",
      status: 1,
    });
  };

  const handleEdit = (slider) => {
    setEditingSlider(slider);
    setShowEditForm(true);
    setShowAddForm(false);
    const original = {
      image: null,
      category_id: slider.category_id,
      status: slider.status === "active" ? 1 : 0,
    };
    setOriginalData(original);
    setFormData(original);
  };

  const handleDelete = (slider) => {
    setDeletingSlider(slider);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (deletingSlider) {
      dispatch(deleteSlider(deletingSlider.id));
      setShowDeleteModal(false);
      setDeletingSlider(null);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.image && !editingSlider) {
      toast.error("يرجى اختيار صورة");
      return;
    }

    if (!formData.category_id) {
      toast.error("يرجى اختيار التصنيف");
      return;
    }

    const submitData = new FormData();
    if (formData.image) {
      submitData.append("image", formData.image);
    }
    submitData.append("category_id", formData.category_id);
    submitData.append("status", formData.status);

    if (editingSlider) {
      dispatch(updateSlider({ id: editingSlider.id, data: submitData }));
    } else {
      dispatch(addSlider(submitData));
    }
  };

  const Pagination = () => {
    if (!meta || meta.last_page <= 1) return null;
    const s = Math.max(1, currentPage - 2), e = Math.min(meta.last_page, currentPage + 2);
    const pages = [];
    for (let i = s; i <= e; i++) {
      pages.push(
        <button key={i} onClick={() => i !== currentPage && handlePageChange(i)} disabled={loading}
          style={{
            width: 36, height: 36, borderRadius: 8, border: "none", fontWeight: i === currentPage ? 700 : 400,
            background: i === currentPage ? "var(--red-color)" : "#f1f5f9", color: i === currentPage ? "#fff" : "#475569", cursor: "pointer"
          }}>
          {i}
        </button>
      );
    }
    const nb = (dis, lbl, fn) => (
      <button onClick={fn} disabled={dis}
        style={{ padding: "0 14px", height: 36, borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", color: dis ? "#cbd5e1" : "#475569", cursor: dis ? "not-allowed" : "pointer", fontWeight: 500 }}>
        {lbl}
      </button>
    );
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 6, marginTop: 24 }}>
        {nb(currentPage === 1, "السابق", () => handlePageChange(currentPage - 1))}
        <div style={{ display: "flex", gap: 4 }}>{pages}</div>
        {nb(currentPage === meta.last_page, "التالي", () => handlePageChange(currentPage + 1))}
      </div>
    );
  };

  const inputStyle = {
    width: "100%",
    padding: "10px 14px",
    borderRadius: 9,
    border: "1.5px solid #e2e8f0",
    fontSize: 14,
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "inherit",
    background: "#fff",
    transition: "border-color 0.2s, box-shadow 0.2s"
  };

  const labelStyle = {
    fontSize: 12,
    fontWeight: 700,
    color: "#64748b",
    display: "block",
    marginBottom: 6,
    textAlign: "right"
  };

  const focusHandler = (e) => {
    e.target.style.borderColor = "var(--main-color)";
    e.target.style.boxShadow = "0 0 0 3px rgba(228, 109, 23, 0.1)";
  };

  const blurHandler = (e) => {
    e.target.style.borderColor = "#e2e8f0";
    e.target.style.boxShadow = "none";
  };

  const overlay = { position: "fixed", inset: 0, background: "rgba(15,23,42,.55)", zIndex: 1040, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 };

  if (loading && (!sliders || !sliders.data || sliders.data.length === 0)) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "50vh" }}>
        <div className="spinner-border text-danger" role="status">
          <span className="visually-hidden">جاري التحميل...</span>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: "4px 0", direction: "rtl" }}>
      <style>{`
        .hero-table {
          width: 100%;
          border-collapse: collapse;
          text-align: right;
        }
        .hero-table th {
          background-color: #f8fafc !important;
          color: #475569 !important;
          font-weight: 700 !important;
          font-size: 13px !important;
          padding: 14px 16px !important;
          border-bottom: 1px solid #e2e8f0 !important;
          text-align: center !important;
        }
        .hero-table td {
          padding: 14px 16px !important;
          font-size: 14px !important;
          vertical-align: middle !important;
          border-bottom: 1px solid #f1f5f9 !important;
          text-align: center !important;
        }
        .hero-custom-select {
          width: 100% !important;
          padding: 10px 14px !important;
          padding-left: 40px !important;
          border-radius: 9px !important;
          border: 1.5px solid #e2e8f0 !important;
          font-size: 14px !important;
          outline: none !important;
          box-sizing: border-box !important;
          background-color: #fff !important;
          appearance: none !important;
          -webkit-appearance: none !important;
          -moz-appearance: none !important;
          background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'></polyline></svg>") !important;
          background-repeat: no-repeat !important;
          background-position: left 14px center !important;
          background-size: 16px !important;
          cursor: pointer !important;
          transition: border-color 0.2s, box-shadow 0.2s !important;
        }
        .hero-custom-select:focus {
          border-color: var(--main-color) !important;
          box-shadow: 0 0 0 3px rgba(228, 109, 23, 0.1) !important;
        }
      `}</style>

      {/* ── Header ─────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <div>
          <h4 style={{ margin: 0, fontWeight: 800, color: "#0f172a", fontSize: 20, display: "flex", alignItems: "center", gap: 8 }}>
            🖼 سلايدرات واجهة البداية (Hero Sliders)
          </h4>
          <p style={{ margin: "2px 0 0", color: "#64748b", fontSize: 13 }}>تعديل ومراقبة وإضافة صور البانرات الترويجية المتحركة بأعلى الصفحة الرئيسية</p>
        </div>
        <PermissionMotionButton
          permission="create-sliders"
          onClick={handleAdd}
          style={{
            display: "flex", alignItems: "center", gap: 8, padding: "9px 20px", borderRadius: 10, border: "none",
            background: "linear-gradient(135deg,var(--main-color),var(--main-light))", color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 14
          }}
        >
          <FaPlus size={12} /> إضافة سلايدر جديد
        </PermissionMotionButton>
      </div>

      {/* Add/Edit Form */}
      {(showAddForm || showEditForm) && (
        <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", padding: 24, marginBottom: 24, boxShadow: "0 1px 4px rgba(0,0,0,.04)" }}>
          <h5 style={{ fontWeight: 800, color: "#0f172a", marginBottom: 16 }}>
            {editingSlider ? <><FaEdit /> تعديل السلايدر</> : <><FaPlus size={12} /> إضافة سلايدر جديد</>}
          </h5>
          <form onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label style={labelStyle}>صورة البانر *</label>
                <input
                  type="file"
                  style={inputStyle}
                  name="image"
                  accept="image/*"
                  onChange={handleInputChange}
                  required={!editingSlider}
                  onFocus={focusHandler}
                  onBlur={blurHandler}
                />
                {editingSlider && (
                  <div style={{ marginTop: 10 }}>
                    <small style={{ color: "#64748b" }}>الصورة الحالية:</small>
                    <br />
                    <img
                      src={editingSlider.image}
                      alt="Current banner preview"
                      style={{ width: "120px", height: "80px", objectFit: "cover", borderRadius: "8px", border: "1.5px solid #cbd5e1" }}
                      className="mt-1"
                    />
                  </div>
                )}
              </div>
              <div className="col-md-6 mb-3">
                <label style={labelStyle}>التصنيف المرتبط بالبانر *</label>
                <select
                  className="hero-custom-select"
                  name="category_id"
                  value={formData.category_id ? String(formData.category_id) : ""}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">اختر التصنيف</option>
                  {categories?.map((category) => (
                    <option key={category.id} value={String(category.id)}>
                      {category.name_ar}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-6 mb-3">
                <label style={labelStyle}>حالة التفعيل *</label>
                <select
                  className="hero-custom-select"
                  name="status"
                  value={formData.status !== undefined && formData.status !== null ? String(formData.status) : "1"}
                  onChange={handleInputChange}
                  required
                >
                  <option value="1">مفعل (نشط ويظهر للمستخدمين)</option>
                  <option value="0">غير مفعل</option>
                </select>
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, borderTop: "1px solid #f1f5f9", marginTop: 15, paddingTop: 15 }}>
              <button
                type="button"
                onClick={() => {
                  setShowAddForm(false);
                  setShowEditForm(false);
                  setEditingSlider(null);
                  setFormData({ image: null, category_id: "", status: 1 });
                }}
                style={{ padding: "8px 20px", borderRadius: 9, border: "1px solid #cbd5e1", background: "#fff", color: "#475569", fontWeight: 700, cursor: "pointer", fontSize: 13 }}
              >
                إلغاء
              </button>
              <button
                type="submit"
                disabled={loading || !hasChanges}
                style={{ padding: "8px 26px", borderRadius: 9, border: "none", background: hasChanges ? "linear-gradient(135deg,var(--main-color),var(--main-light))" : "#cbd5e1", color: "#fff", fontWeight: 700, cursor: hasChanges ? "pointer" : "not-allowed", fontSize: 13 }}
              >
                {loading ? 'جاري الحفظ...' : (editingSlider ? 'حفظ التعديلات' : 'إضافة البانر')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── Table Container ────────────────────────────── */}
      <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,.04)", padding: 0 }}>
        {(!sliders || !sliders.data || sliders.data.length === 0) ? (
          <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8" }}>
            <FaImage size={48} className="text-muted mb-3" />
            <p style={{ marginTop: 12, fontWeight: 600, fontSize: 15, color: "#64748b" }}>لا توجد سلايدرات مسجلة حالياً</p>
          </div>
        ) : (
          <div style={{ padding: 0 }}>
            <div className="table-responsive">
              <table className="hero-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>الصورة</th>
                    <th>التصنيف المرتبط</th>
                    <th>الحالة</th>
                    <th>الإجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {sliders.data.map((slider, index) => (
                    <tr key={slider.id}>
                      <td>
                        {meta ? (meta.current_page - 1) * meta.per_page + index + 1 : index + 1}
                      </td>
                      <td>
                        <img
                          src={slider.image}
                          alt="Banner preview"
                          style={{ width: "90px", height: "60px", objectFit: "cover", borderRadius: "8px", border: "1.5px solid #e2e8f0" }}
                        />
                      </td>
                      <td>
                        <span style={{ fontSize: 12, fontWeight: 700, color: "#4f46e5", background: "#e0e7ff", padding: "3px 12px", borderRadius: 20 }}>
                          {slider.name_ar}
                        </span>
                      </td>
                      <td>
                        <span style={{ fontSize: 11, fontWeight: 700, color: slider.status === "active" ? "#15803d" : "#64748b", background: slider.status === "active" ? "#d1fae5" : "#f1f5f9", padding: "3px 10px", borderRadius: 20 }}>
                          {slider.status === "active" ? "مفعل" : "غير مفعل"}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: "inline-flex", gap: 5, justifyContent: "center" }}>
                          <PermissionButton
                            onClick={() => handleEdit(slider)}
                            permission="edit-sliders"
                            title="تعديل"
                            style={{ width: 30, height: 30, borderRadius: 8, border: "1px solid var(--warning-color)", background: "#fefce8", color: "var(--warning-color)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                          >
                            <FaEdit size={14} />
                          </PermissionButton>
                          <PermissionButton
                            onClick={() => handleDelete(slider)}
                            permission="delete-sliders"
                            title="حذف"
                            style={{ width: 30, height: 30, borderRadius: 8, border: "1px solid #fecaca", background: "#fef2f2", color: "var(--red-color)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                          >
                            <FaTrash size={14} />
                          </PermissionButton>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ padding: "0 16px 20px" }}>
              <Pagination meta={meta} currentPage={currentPage} loading={loading} onChange={handlePageChange} />
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div style={overlay}>
          <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 480, boxShadow: "0 25px 60px rgba(0,0,0,.25)", overflow: "hidden" }}>
            <div style={{ padding: "18px 24px", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontWeight: 700, fontSize: 17, display: "flex", alignItems: "center", gap: 6 }}><FaTrash /> تأكيد الحذف</span>
              <button onClick={() => { setShowDeleteModal(false); setDeletingSlider(null); }} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#94a3b8" }}>✕</button>
            </div>
            <div style={{ padding: "18px 24px" }}>
              <p style={{ margin: 0, color: "#334155" }}>هل أنت متأكد من حذف هذا السلايدر الترويجي؟</p>
              {deletingSlider?.image && (
                <div style={{ marginTop: 10, textAlign: "center" }}>
                  <img
                    src={deletingSlider.image}
                    alt="Deleting Banner preview"
                    style={{ width: "180px", height: "100px", objectFit: "cover", borderRadius: "8px", border: "1.5px solid #cbd5e1" }}
                  />
                </div>
              )}
              <p style={{ margin: "10px 0 0", color: "var(--red-color)", fontSize: 13 }}>⚠ هذا الإجراء لا يمكن التراجع عنه.</p>
            </div>
            <div style={{ padding: "14px 24px", background: "#f8fafc", display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button onClick={() => { setShowDeleteModal(false); setDeletingSlider(null); }} style={{ padding: "8px 20px", borderRadius: 9, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontWeight: 600, cursor: "pointer" }}>إلغاء</button>
              <button onClick={confirmDelete} style={{ padding: "8px 20px", borderRadius: 9, border: "none", background: "var(--red-color)", color: "#fff", fontWeight: 600, cursor: "pointer" }}>حذف السلايدر</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Hero;
